import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { TreeStructure } from "@phosphor-icons/react";

/** Minimal, indented org-chart tree from GET /hr/orgchart.
 *  Uses a flat/iterative rendering to avoid Babel recursion issues with
 *  visual-edits on recursive JSX components. */
export default function OrgChartTab() {
  const [data, setData] = useState({ roots: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/hr/orgchart")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Flatten the tree DFS into a linear list with level markers
  const flat = flatten(data.roots || []);

  if (loading) return <p className="text-sm text-slate-400 text-center py-16">Cargando organigrama…</p>;
  return (
    <div className="space-y-3" data-testid="hr-orgchart">
      <div className="flex items-center gap-2">
        <TreeStructure size={18} weight="duotone" className="text-indigo-700" />
        <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Organigrama</p>
        <span className="text-xs text-slate-400 font-mono">({data.total} activos)</span>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        {flat.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">
            Sin colaboradores activos. Asigne el campo &quot;Reporta a&quot; al editar un colaborador para construir el organigrama.
          </p>
        ) : (
          <ul className="space-y-0.5">
            {flat.map((row) => {
              const roleColor = ROLE_COLORS[row.node.role] || ROLE_COLORS.default;
              const indent = row.level * 22;
              return (
                <li
                  key={row.node.id}
                  data-testid={`org-node-${row.node.id}`}
                  style={{ marginLeft: indent + "px" }}
                  className="flex items-center gap-2 py-1.5 border-l-2 border-slate-100 pl-3"
                >
                  <span className={"text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border " + roleColor}>{row.node.role || "-"}</span>
                  <p className="text-sm font-semibold text-slate-900">{row.node.name}</p>
                  <p className="text-xs text-slate-500 truncate">{row.node.position} · {row.node.area || "-"}</p>
                  {row.node.crew && row.node.crew !== "N/A" && (
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">C-{row.node.crew}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

const ROLE_COLORS = {
  gerente_proyecto: "bg-blue-100 text-blue-900 border-blue-200",
  jefatura: "bg-indigo-100 text-indigo-800 border-indigo-200",
  residente_mina: "bg-purple-100 text-purple-800 border-purple-200",
  operativo: "bg-slate-100 text-slate-700 border-slate-200",
  default: "bg-slate-100 text-slate-700 border-slate-200",
};

function flatten(roots) {
  const out = [];
  const stack = roots.slice().reverse().map((n) => ({ node: n, level: 0 }));
  while (stack.length) {
    const cur = stack.pop();
    out.push(cur);
    const kids = cur.node.reports || [];
    for (let i = kids.length - 1; i >= 0; i--) {
      stack.push({ node: kids[i], level: cur.level + 1 });
    }
  }
  return out;
}
